import apiClient from '@/api/client'
import { prebillTenderFromWire, type UnpaidTenderTotals } from '@/lib/printCustomerReceipt'

/**
 * F-MM-N28 — the money on an initial (unpaid) bill, priced by the server.
 *
 * `mergePricingSettings` fills a missing /counter/pricing response with
 * hardcoded defaults (15/5/15, 10% service charge, no POS fee). That is fine
 * for a first paint, but on 2026-09-30 NinetyPlus (0% service, Rs 1 POS fee)
 * printed an initial bill from those defaults, the cashier keyed its Card
 * figure into the terminal, and the guest paid Rs 2,910 for an order the POS
 * recorded, and filed, at Rs 2,647.
 *
 * Anything a customer pays from now comes from GET /counter/orders/:id/prebill,
 * which runs the checkout's own recompute. No fallback: if it cannot be
 * loaded, the caller refuses to print.
 */
export type ServerPrebill =
  | { ok: true; cash: UnpaidTenderTotals | null; card: UnpaidTenderTotals | null; online: UnpaidTenderTotals | null }
  | { ok: false; reason: string }

export async function loadServerPrebill(orderId: string): Promise<ServerPrebill> {
  try {
    const res = await apiClient.getOrderPrebill(orderId)
    if (!res.success || !res.data) {
      return { ok: false, reason: res.message || 'Could not price this bill' }
    }
    const cash = prebillTenderFromWire(res.data.cash)
    const card = prebillTenderFromWire(res.data.card)
    const online = prebillTenderFromWire(res.data.online)
    if (!cash && !card) {
      return { ok: false, reason: 'Could not price this bill' }
    }
    return { ok: true, cash, card, online }
  } catch (e) {
    const msg = e instanceof Error && e.message ? e.message : ''
    return { ok: false, reason: msg || 'Could not reach the server to price this bill' }
  }
}
